import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { useAuth } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { url } from "../../constants/url";

import ClientDetailsSidebar from "../../components/siteCheck/ViewChecklistsDetails";

// Technical Dashboard component for Engineers (Matches Individual Client Layout)
const TechnicalDashboard = () => {
  const { userData, token, hasRole } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalRequests: 0,
    completedRequests: 0,
  });

  const [individualStats, setIndividualStats] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  useEffect(() => {
    const fetchEngineerStats = async () => {
      try {
        let endpoint = `${url}/api/dashboard/individual-client-stats`;
        
        const res = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.data.success) {
          setIndividualStats(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch engineer dashboard stats:", err);
      }
    };
    if (token) {
        fetchEngineerStats();
    }
  }, [userData, token, hasRole]);

  const [recentRequests, setRecentRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);

  useEffect(() => {
    const fetchRequests = async () => {
      setRequestsLoading(true);
      try {
        const params = {};
        if (hasRole("ENGINEER") && userData?._id) {
            params.engineer = userData._id;
        }

        const res = await axios.get(`${url}/api/fir/requests`, {
          params,
          headers: { Authorization: token ? `Bearer ${token}` : undefined }
        });

        if (res.data.success) {
          const fetchedData = res.data.data || [];
          setRecentRequests(fetchedData);
          setStats(prev => ({
            ...prev,
            totalRequests: fetchedData.length,
            completedRequests: fetchedData.filter(r => 
              r.hasReport || 
              (r.reportCount && r.reportCount > 0) || 
              r.firStatus === "Completed Inspection" || 
              r.firStatus === "Report Submitted" || 
              r.projectStatus === "completed" || 
              r.status === "FINALREPORT"
            ).length
          }));
        }
      } catch (err) {
        console.error("Failed to fetch engineer FIR requests:", err);
      } finally {
        setRequestsLoading(false);
      }
    };
    if (token) {
        fetchRequests();
    }
  }, [userData, token, hasRole]);

  const totalSqft = individualStats?.totalSqftSent ?? Object.values(individualStats?.planBreakdown || {}).reduce((acc, curr) => acc + (curr.totalSqftSent || 0), 0);
  const globalTotalRequests = Object.values(individualStats?.planBreakdown || {}).reduce((acc, curr) => acc + (curr.totalRequestsCount || 0), 0);

  return (
    <Box>
      <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY, mb: 3 }}>
        Welcome, {userData?.name || "Engineer"}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          mb: 4,
          "& > div": {
            width: "250px",
            flexShrink: 0
          }
        }}
      >
        <Box>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: '100%' }}>
              <Icon name="clients" stroke={2} size={46} color={COLORS.SECONDARY} />
              <Box paddingLeft={2}>
                <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }} paddingBottom={0.5}>
                  {individualStats?.totalIndividualClients ?? 0}
                </Typography>
                <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                  Total Individual Clients
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: '100%' }}>
              <Icon name="projects" stroke={2} size={46} color={COLORS.SECONDARY} />
              <Box paddingLeft={2}>
                <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }} paddingBottom={0.5}>
                  {globalTotalRequests}
                </Typography>
                <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                  Total Requests
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: '100%' }}>
              <Icon name="wallpaper" stroke={2} size={46} color={COLORS.SECONDARY} />
              <Box paddingLeft={2}>
                <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }} paddingBottom={0.5}>
                  {totalSqft.toLocaleString()}
                </Typography>
                <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                  Total Sq Ft
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Box>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: '100%' }}>
              <Icon name="projects" stroke={2} size={46} color={COLORS.SECONDARY} />
              <Box paddingLeft={2}>
                <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H2, color: COLORS.SECONDARY }} paddingBottom={0.5}>
                  {individualStats?.adminAddedProjects || 0}
                </Typography>
                <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                  Admin Added Projects
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Completed Requests per Property Plan Option */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 2, fontWeight: 700 }}>
          Requests by Property Plan
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)"
            },
            gap: 2
          }}
        >
          {Array.from(new Set([
            "1 BHK", "2 BHK", "2.5 BHK", "3 BHK", "4 BHK", "5 BHK", "Studio", "Bungalow", "Shop / Office (Up to 1000 sq ft)", "Shop / Office (1000-5000 sq ft)",
            ...Object.keys(individualStats?.planBreakdown || {})
          ])).sort((a, b) => {
            const predefinedOrder = [
              "1 BHK", "2 BHK", "2.5 BHK", "3 BHK", "4 BHK", "5 BHK",
              "Studio", "Bungalow", "Shop / Office (Up to 1000 sq ft)", "Shop / Office (1000-5000 sq ft)"
            ];
            const indexA = predefinedOrder.indexOf(a);
            const indexB = predefinedOrder.indexOf(b);
            
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            
            return a.localeCompare(b);
          }).map((planType) => {
            const data = individualStats?.planBreakdown?.[planType] || {
              totalRequestsCount: 0,
              completedCount: 0,
              totalSqftSent: 0,
              completedSqft: 0
            };
            return (
              <Box
                key={planType}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: "#fdfbff",
                  border: `1.5px solid ${COLORS.LIGHT_SECONDARY}60`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 1
                }}
              >
                <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, fontWeight: 700 }}>
                  {planType}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: 1 }}>
                  <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.TEXT, fontWeight: 500 }}>
                    Total Requests:
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "1.3rem", fontWeight: 700, color: COLORS.PRIMARY }}>
                    {data.totalRequestsCount}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mt: 0.5 }}>
                  <Typography sx={{ fontFamily: FONT_FAMILY, ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.MUTED_TEXT, fontWeight: 500, fontSize: "0.85rem" }}>
                    Total Sq Ft:
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: "0.95rem", fontWeight: 700, color: COLORS.SECONDARY }}>
                    {(data.totalSqftSent || 0).toLocaleString()} sq ft
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 5 }}>
        <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
          Recent Inspection Requests
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            ...TYPOGRAPHY.BODY_SMALL,
            color: COLORS.PRIMARY,
            cursor: "pointer",
            fontWeight: 600,
            textDecoration: "underline",
          }}
          onClick={() => navigate("/projects/fir-requests")}
        >
          View All
        </Typography>
      </Box>

      {requestsLoading ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#FFF' }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.MUTED_TEXT, py: 4 }}>
            Loading your recent requests...
          </Typography>
        </Paper>
      ) : recentRequests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#FFF' }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.MUTED_TEXT, py: 4 }}>
            No Field Inspection Requests assigned yet.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {recentRequests.slice(0, 3).map((req, idx) => {
            const propName = req.projectName || req.firDetails?.projectName || req.propertyName || req.firDetails?.propertyName || req.buildingName || "Property Request";
            const propType = req.propertyType || req.firDetails?.propertyType || "";
            const isReportCompleted = req.hasReport || (req.reportCount && req.reportCount > 0) || req.firStatus === "Completed Inspection" || req.firStatus === "Report Submitted" || req.projectStatus === "completed" || req.status === "FINALREPORT";
            const statusStr = isReportCompleted ? "Report Submitted" : (req.firStatus || "Requested to Admin");
            const fullAddr = req.fullAddress || "";
            const reqDate = req.createdAt ? new Date(req.createdAt).toLocaleDateString("en-GB") : "";

            return (
              <Grid item xs={12} sm={6} md={4} key={req._id || idx}>
                {(() => {
                  const targetProjectId = req.associatedProject?._id || req.associatedProject || req.projectId;
                  const engineersList = req.assignedEngineers || req.associatedProject?.assignedEngineers || [];
                  const engineerNames = engineersList.length > 0 
                    ? engineersList.map(eng => (typeof eng === 'object' ? eng.name : eng)).join(", ") 
                    : "Not Assigned";

                  return (
                    <Paper sx={{ 
                      p: 2.5, 
                      borderRadius: 3, 
                      border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, 
                      boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      width: "100%",
                      backgroundColor: "#FFF",
                      overflow: "hidden"
                    }}>
                      <Box sx={{ flexGrow: 1 }}>
                        {/* Title & Type */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5, gap: 1 }}>
                          <Typography sx={{ 
                            fontFamily: FONT_FAMILY, 
                            fontSize: "1rem", 
                            color: COLORS.PRIMARY, 
                            fontWeight: 700, 
                            lineHeight: 1.3,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden"
                          }}>
                            {propName}
                          </Typography>
                          {propType && (
                            <Typography sx={{ 
                              fontSize: "10px", 
                              fontWeight: 700, 
                              color: COLORS.SECONDARY, 
                              backgroundColor: "#f3ecf8", 
                              px: 1, 
                              py: 0.3, 
                              borderRadius: 1,
                              whiteSpace: "nowrap"
                            }}>
                              {propType}
                            </Typography>
                          )}
                        </Box>

                        {/* Address */}
                        {fullAddr && (
                          <Typography sx={{ 
                            fontSize: "11px", 
                            color: COLORS.TEXT, 
                            mb: 1.5, 
                            display: "-webkit-box", 
                            WebkitLineClamp: 2, 
                            WebkitBoxOrient: "vertical", 
                            overflow: "hidden"
                          }}>
                            {fullAddr}
                          </Typography>
                        )}

                        <Divider sx={{ mb: 1.5, borderColor: `${COLORS.LIGHT_SECONDARY}20` }} />

                        {/* Details Section */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography sx={{ fontSize: "11px", color: COLORS.MUTED_TEXT }}>
                              Requested Date:
                            </Typography>
                            <Typography sx={{ fontSize: "11px", fontWeight: 600, color: COLORS.PRIMARY }}>
                              {reqDate}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <Typography sx={{ fontSize: "11px", color: COLORS.MUTED_TEXT, mr: 1 }}>
                              Assigned Engineer:
                            </Typography>
                            <Typography sx={{ 
                              fontSize: "11px", 
                              fontWeight: 600, 
                              color: engineerNames === "Not Assigned" ? COLORS.MUTED_TEXT : COLORS.SECONDARY,
                              textAlign: "right",
                              maxWidth: "60%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}>
                              {engineerNames}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Footer Actions */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 1.5, borderTop: "1px dashed #f0f0f0", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                          label={statusStr}
                          size="small"
                          sx={{
                            backgroundColor: isReportCompleted ? "#E8F5E9" : "#FFF8E1",
                            color: isReportCompleted ? "#2E7D32" : "#F57F17",
                            fontWeight: 700,
                            fontSize: "10px",
                            height: 22,
                            borderRadius: 1.5,
                            border: `1px solid ${isReportCompleted ? "#C8E6C9" : "#FFE082"}`
                          }}
                        />

                        {targetProjectId ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => navigate(`/technical/project-details/${targetProjectId}`)}
                            sx={{
                              fontSize: "11px",
                              py: 0.5,
                              px: 1.5,
                              backgroundColor: COLORS.PRIMARY,
                              textTransform: "none",
                              borderRadius: 1.5,
                              fontWeight: 600,
                              boxShadow: "none",
                              "&:hover": { backgroundColor: COLORS.SECONDARY, boxShadow: "none" }
                            }}
                          >
                            View Details
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/technical/project-details/${req._id}`)}
                            sx={{
                              fontSize: "11px",
                              py: 0.5,
                              px: 1.5,
                              borderColor: COLORS.PRIMARY,
                              color: COLORS.PRIMARY,
                              textTransform: "none",
                              borderRadius: 1.5,
                              fontWeight: 600,
                              "&:hover": { backgroundColor: "#f3ecf8", borderColor: COLORS.PRIMARY }
                            }}
                          >
                            View Request
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  );
                })()}
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Request / Project Details Drawer */}
      <ClientDetailsSidebar
        open={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedProjectId(null);
        }}
        projectId={selectedProjectId}
      />
    </Box>
  );
};

export default TechnicalDashboard;
